"use client";

import Link from "next/link";
import Image from "next/image";
import { DISPLAY_SERVICES } from "@/lib/display-services";
import { Reveal } from "@/components/ui/Reveal";
import { ArrowRight, Star } from "lucide-react";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export function DetailedServices() {
    // Show 6 services in a compact view
    const displayedServices = DISPLAY_SERVICES.slice(0, 6);

    // Animation Refs
    const containerRef = useRef<HTMLElement>(null);
    const bgRef = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        gsap.to(bgRef.current, {
            x: "15%",
            ease: "none",
            scrollTrigger: {
                trigger: containerRef.current,
                start: "top bottom",
                end: "bottom top",
                scrub: true
            }
        });
    }, { scope: containerRef });

    return (
        <section ref={containerRef} className="relative flex flex-col justify-center overflow-hidden bg-[#000510] py-12 lg:py-16 xl:py-20">
            <div
                ref={bgRef}
                className="absolute inset-y-0 -left-[40%] w-[180%] z-0 will-change-transform"
            >
                <Image
                    src="/images/backgrounds/row-bgimage-3.png"
                    alt="Background Pattern"
                    fill
                    className="object-cover object-top opacity-60 blur-[3px]"
                    quality={100}
                    priority
                />
            </div>

            <div className="container mx-auto px-4 relative z-10">
                {/* Header Section - Compact */}
                <div className="text-center max-w-4xl mx-auto mb-8 lg:mb-10">
                    <Reveal width="100%">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/10 mb-4 shadow-lg">
                            <Star className="w-3 h-3 text-white fill-white" />
                            <span className="text-white font-bold uppercase tracking-widest text-[10px]">SOLUTIONS</span>
                            <Star className="w-3 h-3 text-white fill-white" />
                        </div>
                    </Reveal>
                    <Reveal width="100%" delay={0.2}>
                        <h2 className="text-2xl md:text-4xl font-bold font-sans mb-2 text-white leading-tight drop-shadow-md">
                            Crafting innovative <span className="text-blue-400">digital solutions</span>
                        </h2>
                    </Reveal>
                </div>

                {/* Services Grid - Max width constrained to prevent cards from being too wide */}
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
                        {displayedServices.map((service, index) => {
                            return (
                                <Reveal key={service.slug} delay={index * 0.1}>
                                    <Link href={`/services/${service.slug}`} className="block h-full group">
                                        <div
                                            className="bg-white/10 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden h-full flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl shadow-black/40 hover:bg-white/15 hover:border-white/20 ring-1 ring-white/5"
                                        >
                                            {/* Image Section - Fixed Aspect Ratio/Height */}
                                            <div className="relative w-full h-36 min-[480px]:h-40 overflow-hidden flex-shrink-0 bg-white/5">
                                                <Image
                                                    src={service.image || "/images/placeholder.jpg"}
                                                    alt={service.title}
                                                    fill
                                                    sizes="(max-width: 480px) 100vw, (max-width: 1024px) 50vw, 400px"
                                                    quality={70}
                                                    className="object-cover transition-transform duration-700 group-hover:scale-110 opacity-90 group-hover:opacity-100"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-[#000510] via-transparent to-transparent opacity-60"></div>
                                            </div>

                                            {/* Content Section - Flex Grow to fill height */}
                                            <div className="p-4 flex flex-col justify-between h-full relative w-full flex-grow">
                                                <div>
                                                    <h3 className="text-lg font-bold text-white mb-2 leading-tight group-hover:text-blue-400 transition-colors line-clamp-1 drop-shadow-sm">
                                                        {service.title}
                                                    </h3>

                                                    <p className="text-gray-300/90 text-xs leading-relaxed mb-3 line-clamp-3 font-medium">
                                                        {service.shortDescription}
                                                    </p>
                                                </div>

                                                <div className="mt-auto">
                                                    <div className="inline-flex items-center text-xs font-bold text-white/80 hover:text-blue-400 transition-colors group/link border-b border-white/20 pb-0.5 hover:border-blue-400">
                                                        Read more
                                                        <ArrowRight className="w-3 h-3 ml-1 transition-transform group-hover/link:translate-x-1" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>

                {/* Bottom View All Link - Compact margin */}
                <div className="text-center">
                    <Link
                        href="/services"
                        className="inline-flex items-center justify-center px-6 py-2.5 text-sm font-bold text-white transition-all duration-200 bg-blue-600 border-2 border-transparent rounded-full hover:bg-transparent hover:border-blue-600 hover:text-white shadow-lg shadow-blue-900/50"
                    >
                        View All Services
                        <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                </div>
            </div>
        </section>
    );
}
